import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAccountApi } from '@/services/AccountApi';
import { type IWebauthnCeremony, CeremonyCancelled } from '@/services/WebauthnCeremony';
import { type IQr } from '@/services/Qr';
import { type ILogger } from '@/services/Logger';
import { type MfaStatus } from '@/domain/mfa';

/** The in-flight TOTP enrollment as the view renders it (the ticket stays internal). */
export interface PendingTotp {
  secret: string;
  otpauthUri: string;
  /** The otpauth URI as a QR `data:` URL, drawn client-side (the secret never leaves the page). */
  qrDataUrl: string;
}

/**
 * The account MFA ViewModel (U1): factor list (TOTP, security keys, recovery-code count), the
 * TOTP enrollment ceremony (start -> QR + secret -> confirm with a live code), WebAuthn key
 * add/remove (the browser ceremony rides `IWebauthnCeremony`), and recovery-code regenerate.
 *
 * Two server rules shape the state machine: every factor mutation is RE-AUTH-gated (a current
 * password rides each call), and enroll/remove REVOKE every session (the Sub-watermark rule).
 * `reauthRequired` flips true and the view must prompt a re-login; only `load` and `regenerate`
 * leave the session alive. Recovery codes appear ONCE, in the response that minted them
 * (`recoveryCodes` until dismissed), never again.
 */
export class MfaStore {
  private _status: MfaStatus | null = null;
  private _loading = false;
  private _busy = false;
  private _error: string | null = null;
  private _notice: string | null = null;
  private _pendingTotp: PendingTotp | null = null;
  private _enrollTicket: string | null = null;
  private _recoveryCodes: string[] | null = null;
  private _reauthRequired = false;

  constructor(
    private api: IAccountApi,
    private ceremony: IWebauthnCeremony,
    private qr: IQr,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get status(): MfaStatus | null {
    return this._status;
  }
  get loading(): boolean {
    return this._loading;
  }
  get busy(): boolean {
    return this._busy;
  }
  get error(): string | null {
    return this._error;
  }
  get notice(): string | null {
    return this._notice;
  }
  get pendingTotp(): PendingTotp | null {
    return this._pendingTotp;
  }
  /** The one-time batch from a first-factor enrollment or a regenerate; null once dismissed. */
  get recoveryCodes(): string[] | null {
    return this._recoveryCodes;
  }
  /** True after an enroll/remove revoked every session: the view prompts a re-login. */
  get reauthRequired(): boolean {
    return this._reauthRequired;
  }
  /** Whether this browser can run the security-key ceremony (the add button hides when not). */
  get webauthnSupported(): boolean {
    return this.ceremony.supported();
  }

  async load(): Promise<void> {
    this._loading = true;
    this._error = null;
    try {
      const status = await this.api.mfaStatus();
      runInAction(() => (this._status = status));
    } catch (e) {
      this.fail('load security settings', e);
    } finally {
      runInAction(() => (this._loading = false));
    }
  }

  /** Start TOTP enrollment: re-auth, then hold the pending secret + QR for the confirm step. */
  async totpStart(currentPassword: string): Promise<void> {
    if (!currentPassword) {
      this._error = 'your current password is required';
      return;
    }
    await this.run(null, async () => {
      const start = await this.api.totpStart(currentPassword);
      const qrDataUrl = await this.qr.toSvgDataUrl(start.otpauth_uri);
      runInAction(() => {
        this._pendingTotp = { secret: start.secret, otpauthUri: start.otpauth_uri, qrDataUrl };
        this._enrollTicket = start.enroll_ticket;
      });
    });
  }

  /** Confirm with a live code. Success ENROLLS, mints first-factor recovery codes (shown once),
   * and revokes every session. */
  async totpConfirm(code: string): Promise<void> {
    const ticket = this._enrollTicket;
    if (!ticket) {
      this._error = 'no enrollment in progress';
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      this._error = 'enter the 6-digit code from your authenticator app';
      return;
    }
    await this.run('Authenticator enrolled.', async () => {
      const codes = await this.api.totpConfirm(ticket, code.trim());
      runInAction(() => {
        this._pendingTotp = null;
        this._enrollTicket = null;
        this._recoveryCodes = codes;
        this._reauthRequired = true;
      });
    });
  }

  cancelTotp(): void {
    this._pendingTotp = null;
    this._enrollTicket = null;
    this._error = null;
  }

  async totpRemove(currentPassword: string): Promise<void> {
    if (!currentPassword) {
      this._error = 'your current password is required';
      return;
    }
    await this.run('Authenticator removed.', async () => {
      await this.api.totpRemove(currentPassword);
      runInAction(() => (this._reauthRequired = true));
    });
  }

  /** Add a security key / passkey: re-auth -> server challenge -> the browser ceremony -> finish.
   * Success enrolls, mints first-factor recovery codes (shown once), and revokes every session. */
  async webauthnAdd(currentPassword: string, name: string): Promise<void> {
    const trimmed = name.trim();
    if (!currentPassword) {
      this._error = 'your current password is required';
      return;
    }
    if (!trimmed || trimmed.length > 64) {
      this._error = 'give the key a name (1-64 characters)';
      return;
    }
    if (!this.ceremony.supported()) {
      this._error = 'this browser cannot enroll a security key';
      return;
    }
    await this.run('Security key enrolled.', async () => {
      const start = await this.api.webauthnStart(currentPassword);
      const credential = await this.ceremony.create(start.ccr);
      const codes = await this.api.webauthnFinish(start.reg_ticket, trimmed, credential);
      runInAction(() => {
        this._recoveryCodes = codes;
        this._reauthRequired = true;
      });
    });
  }

  async webauthnRemove(credId: string, currentPassword: string): Promise<void> {
    if (!currentPassword) {
      this._error = 'your current password is required';
      return;
    }
    await this.run('Security key removed.', async () => {
      await this.api.webauthnRemove(credId, currentPassword);
      runInAction(() => (this._reauthRequired = true));
    });
  }

  /** A fresh batch REPLACES the old set (shown once). Does NOT revoke the session. */
  async regenerateCodes(currentPassword: string): Promise<void> {
    if (!currentPassword) {
      this._error = 'your current password is required';
      return;
    }
    await this.run('New recovery codes generated. The old ones no longer work.', async () => {
      const codes = await this.api.regenerateRecoveryCodes(currentPassword);
      runInAction(() => (this._recoveryCodes = codes));
      await this.load();
    });
  }

  dismissCodes(): void {
    this._recoveryCodes = null;
  }

  private async run(successNotice: string | null, fn: () => Promise<void>): Promise<void> {
    // The dead-bearer guard lives HERE, not only in the view (rule 18: behaviour must be store-
    // testable): after a revoking mutation every further call would 401 confusingly on the dead
    // token, so the store goes inert until the re-login.
    if (this._reauthRequired) return;
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      await fn();
      if (successNotice) runInAction(() => (this._notice = successNotice));
    } catch (e) {
      if (e instanceof CeremonyCancelled) {
        runInAction(() => (this._error = 'The security key prompt was cancelled or timed out. Try again.'));
        return;
      }
      this.fail(successNotice ? successNotice.replace(/\.$/, '') : 'the request', e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  private fail(what: string, e: unknown): void {
    this.logger.error(`${what} failed`, e);
    runInAction(() => (this._error = `${what} failed: ${e instanceof Error ? e.message : String(e)}`));
  }
}

export const TOKEN_MfaStore = token<MfaStore>('MfaStore');
